import React, { useEffect } from 'react';
import { Prompt, useParams } from 'react-router-dom';
import { Box, FormProvider, Preloader } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { Accordion } from '@/components/elements/Accordion';
import { BlockWrapper } from '@/components/wrappers';
import ActivityLog from '@/modules/ActivityLog';
import { useBreadcrumbs } from '@/modules/core/hooks';
import theme from '@/styles/theme';

import { PayoutDefinitionDeleteModal } from '../PayoutDefinitionDeleteModal/PayoutDefinitionDeleteModal';
import { useGetPayoutDefinitionQuery } from '../queries';

import { BasicPropertiesContainer } from './widgets/BasicProperties';
import { FinalSetupContainer } from './widgets/FinalSetup';
import { PayoutOrderContainer, PayoutOrderHeaderActions } from './widgets/PayoutOrder';
import { usePayoutOrderModalsStore } from './widgets/PayoutOrder/stores/usePayoutOrderModals.store';
import { DangerZone } from './components';
import { useEditMode, usePayoutDefinitionSetupForm } from './hooks';
import { PayoutDefinitionField } from './PayoutDefinitionSetup.types';

const PayoutDefinitionSetupContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { formattedData, isLoading } = useGetPayoutDefinitionQuery(id);
  const { isEditMode } = useEditMode();
  const resetPayoutOrderModals = usePayoutOrderModalsStore((state) => state.reset);
  const isSaved = usePayoutOrderModalsStore((state) => state.isSaved);

  // Reset payout order UI state (validation errors, modals) on mount/unmount
  useEffect(() => {
    resetPayoutOrderModals();
    return () => resetPayoutOrderModals();
  }, [resetPayoutOrderModals]);

  const { methods, canBeLimited } = usePayoutDefinitionSetupForm({ data: formattedData });
  const {
    formState: { isDirty, dirtyFields },
  } = methods;
  const hasUserChanges = isDirty && Object.keys(dirtyFields).length > 0;

  const watchedCategories = methods.watch(PayoutDefinitionField.categories);
  const hasServices = (watchedCategories || []).some((category) => category.services.length > 0);
  const hasAnyPlanSelected = (watchedCategories || []).some((category) =>
    category.services.some((service) => service.plans.length > 0)
  );

  useBreadcrumbs(
    [
      {
        route: ROUTES.PROCESSING_DEFINITION,
        title: 'Processing Definition',
      },
      {
        route: ROUTES.PAYOUT_DEFINITIONS,
        title: 'Payout Definitions',
      },
      {
        route: ROUTES.PAYOUT_DEFINITION_VIEW_BY_ID(id),
        title: formattedData?.name || '',
      },
    ],
    [formattedData, id]
  );

  if (!formattedData && isLoading) {
    return (
      <Box width="100%" justify="center" align="center" pad={{ top: 'spacing24' }}>
        <Preloader />
      </Box>
    );
  }

  return (
    <>
      <FormProvider {...methods}>
        <Box>
          <Prompt
            when={!isSaved && isEditMode && hasUserChanges}
            message="You have unsaved changes, are you sure you want to leave?"
          />
          <Accordion />
          <BlockWrapper
            showDivider
            title="Basic Properties"
            moduleAnchorId="basic_properties"
            wrapperStyle={{ marginTop: theme.spacings.xs }}
          >
            <BasicPropertiesContainer isEditMode={isEditMode} canBeLimited={canBeLimited} />
          </BlockWrapper>
          <BlockWrapper
            showDivider
            title="Payout Order"
            moduleAnchorId="payout_order"
            wrapperStyle={{ marginTop: theme.spacings.s }}
            headerActions={
              <PayoutOrderHeaderActions
                isEditMode={isEditMode}
                hasServices={hasServices}
                hasAnyPlanSelected={hasAnyPlanSelected}
              />
            }
          >
            <PayoutOrderContainer
              isEditMode={isEditMode}
              organizationPath={formattedData?.organizationPath}
            />
          </BlockWrapper>
          <BlockWrapper
            showDivider
            title="Activity Log"
            moduleAnchorId="activity_log"
            wrapperStyle={{ marginTop: theme.spacings.s }}
            defaultExpandedValue={false}
          >
            <ActivityLog />
          </BlockWrapper>
          <BlockWrapper
            showDivider
            title="Final Setup"
            moduleAnchorId="final_setup"
            wrapperStyle={{ marginTop: theme.spacings.s }}
            flatMode
          >
            <FinalSetupContainer />
          </BlockWrapper>
          <BlockWrapper
            flatMode
            title="Danger Zone"
            moduleAnchorId="danger_zone"
            wrapperStyle={{ marginTop: theme.spacings.s, height: 'fit-content' }}
          >
            <DangerZone />
          </BlockWrapper>
        </Box>
      </FormProvider>
      <PayoutDefinitionDeleteModal payoutDefinitionId={id} />
    </>
  );
};

export default PayoutDefinitionSetupContainer;
